import { checkImageSize, detectImageFormat, validateBatch, type OutputFormat } from './formats';
import type { ImageDownload } from './engine';
import { validateResizeOptions, type ResizeOptions, type ResizeOutputFormat } from './resize';
export type ConversionProgress = { done: number; total: number; packing?: boolean };

function aborted() { return new DOMException('Đã hủy chuyển ảnh.', 'AbortError'); }

// SVG is rasterized in the browser adapter because ImageBitmap in workers does not decode it reliably.
async function prepareSvg(file: File, signal: AbortSignal, preserveVector: boolean): Promise<File> {
  const image = new Image();
  const url = URL.createObjectURL(new Blob([file], { type: 'image/svg+xml' }));
  const canvas = document.createElement('canvas');
  try {
    await new Promise<void>((resolve, reject) => {
      const cleanup = () => { clearTimeout(timer); signal.removeEventListener('abort', cancel); image.onload = image.onerror = null; };
      const cancel = () => { cleanup(); reject(aborted()); };
      const timer = window.setTimeout(() => { cleanup(); reject(new Error(file.name + ': không đọc được SVG.')); }, 15000);
      image.onload = () => { cleanup(); resolve(); };
      image.onerror = () => { cleanup(); reject(new Error(file.name + ': không đọc được SVG.')); };
      signal.addEventListener('abort', cancel, { once: true });
      if (signal.aborted) { cancel(); return; }
      image.src = url;
    });
    checkImageSize(image.naturalWidth, image.naturalHeight);
    if (preserveVector) return file;
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Không khởi tạo được Canvas.');
    context.drawImage(image, 0, 0);
    const png = await new Promise<Blob>((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Không đọc được SVG.')), 'image/png'));
    return new File([png], file.name.replace(/\.svg$/i, '') + '.png', { type: 'image/png' });
  } finally {
    image.removeAttribute('src');
    URL.revokeObjectURL(url);
    canvas.width = canvas.height = 0;
  }
}

export async function convertImages(files: File[], output: OutputFormat, signal: AbortSignal, onProgress: (progress: ConversionProgress) => void, resize?: ResizeOptions): Promise<ImageDownload> {
  validateBatch(files);
  if (resize) validateResizeOptions(resize);
  const sources: File[] = [];
  for (const file of files) {
    if (signal.aborted) throw aborted();
    sources.push(detectImageFormat(file) === 'svg' ? await prepareSvg(file, signal, output === 'svg' && !resize) : file);
  }
  if (signal.aborted) throw aborted();
  const worker = new Worker('/assets/image-converter-worker.js');
  try {
    return await new Promise<ImageDownload>((resolve, reject) => {
      let timer: ReturnType<typeof setTimeout>;
      const cleanup = () => { clearTimeout(timer); signal.removeEventListener('abort', cancel); };
      const cancel = () => { cleanup(); reject(aborted()); };
      const resetTimer = () => { clearTimeout(timer); timer = setTimeout(() => { cleanup(); reject(new Error('Xử lý quá lâu. Hãy chuyển ít ảnh hơn.')); }, 120000); };
      signal.addEventListener('abort', cancel, { once: true });
      worker.onerror = () => { cleanup(); reject(new Error('Không tải được bộ chuyển ảnh. Tải lại trang rồi thử lại.')); };
      worker.onmessage = ({ data }) => {
        resetTimer();
        if (data.type === 'progress') onProgress({ done: data.done, total: data.total });
        else if (data.type === 'packing') onProgress({ done: sources.length, total: sources.length, packing: true });
        else if (data.type === 'result') { cleanup(); resolve(data.result as ImageDownload); }
        else if (data.type === 'error') { cleanup(); reject(new Error(data.error)); }
      };
      resetTimer();
      if (signal.aborted) { cancel(); return; }
      worker.postMessage({ files: sources, output, resize });
    });
  } finally { worker.terminate(); }
}

export async function resizeImages(files: File[], options: ResizeOptions, output: ResizeOutputFormat, signal: AbortSignal, onProgress: (progress: ConversionProgress) => void): Promise<ImageDownload> {
  return convertImages(files, output, signal, onProgress, options);
}

export function downloadImageResult(result: ImageDownload) {
  const url = URL.createObjectURL(result.blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = result.name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
}
